/**
 * LLM tool definitions backed by the chart capability registry.
 *
 * Why this exists
 * ---------------
 *
 * The unified Excel agent can access chart capabilities through tools.
 * So why also publish this data as an OpenAI tool?
 *
 * 1. **Programmatic grounding for "what can you do?" prompts.** When
 *    the user asks "what charts can you create?" the model today
 *    answers from the prose in its context. With the tool available,
 *    the model can call `list_chart_types({})` and read back the
 *    structured catalog — which means we trust the answer matches the
 *    capability gate at the host boundary, not the model's
 *    paraphrase.
 *
 * 2. **Reusable surface for prompts that DON'T inline the catalog.**
 *    Agent, executor, and verifier prompts all need the same chart
 *    capability picture without each prompt growing a copy of the 27
 *    codes. This module gives them a one-line opt-in.
 *
 * 3. **First production user of tool-calling.** The infrastructure
 *    here (provider-agnostic tool definitions + a single-turn
 *    executor for OpenAI) is the seed for issue #6 in
 *    `excel_ai_issues_and_fixes.md` (`B. Structured output / native
 *    JSON mode`). When we migrate the planner and executor to
 *    function-calling for stricter output, this module is the entry
 *    point.
 *
 * Scope of this PR
 * ----------------
 *
 *  - OpenAI Responses API tool definition + single-turn executor.
 *  - Wired into the OpenAI tool-calling path.
 *  - Anthropic / Gemini intentionally NOT wired: those callers fall
 *    back to the prompt-baked catalog from Phase 3, which means no
 *    regression and the model still has the data — just not as a
 *    tool. The wire shapes for those providers are noted in
 *    `buildToolDefinitionsForProvider` so the next contributor can
 *    add them without re-deriving the tool surface.
 */

import {
  CHART_CAPABILITIES,
  CHART_OUTPUT_MODES,
  type ChartCapability,
  type ChartCategory,
  type ChartMetricBasis,
  type ChartOutputMode,
  type ChartOutputModeCapability,
  type ChartSummaryTableMetadata,
  isExplicitForwardPeRequest,
  listChartCapabilities,
} from "./chartCapabilityRegistry";

/**
 * Provider-neutral description of a tool the model can call. Mirrors
 * OpenAI's "function" shape closely because that's the most expressive
 * common denominator. Per-provider serialisers live in
 * `buildToolDefinitionsForProvider`.
 */
export interface ChartCapabilityToolDef {
  name: string;
  description: string;
  /** JSON-schema object describing the tool's input. */
  inputSchema: Record<string, unknown>;
  /**
   * When true, the OpenAI serializer emits `strict: true` and the API
   * applies constrained decoding — guaranteeing the model's arguments
   * conform to `inputSchema` exactly. Leave unset (or false) for
   * advisory schemas validated at runtime instead.
   */
  strict?: boolean;
}

/**
 * Strongly-typed input shape for `list_chart_types`. The JSON-schema
 * the model sees in `LIST_CHART_TYPES_TOOL.inputSchema` mirrors this.
 */
export interface ListChartTypesInput {
  /** Free-form description of what the user is trying to visualize (e.g. "AAPL price vs MSFT", "revenue by segment"). Used as a relevance hint. */
  userIntent?: string;
  /** Filter to one output mode. Omit (or pass `"any"`) to include all three. */
  outputMode?: ChartOutputMode | "any";
  /** Filter by category (e.g. `"valuation"`, `"native_column"`). Omit for all categories. */
  category?: ChartCategory;
  /** Filter by native-workbook summary-table applicability. */
  summaryTable?: "supported" | "unsupported" | "any";
  /** Cap on the number of results returned. Default 20. Hard max 51 (the full catalog). */
  maxResults?: number;
}

/**
 * The shape the model gets back from a successful `list_chart_types`
 * call. Trimmed compared to the full `ChartCapability` interface —
 * the model doesn't need the alias arrays for picking, just for our
 * server-side `suggestChartCapability` matcher.
 */
export interface ListChartTypesResultEntry {
  id: string;
  displayName: string;
  outputModes: readonly ChartOutputMode[];
  category: ChartCategory;
  description: string;
  /** Exact earnings basis where the distinction applies (for example, trailing/LTM P/E). */
  metricBasis?: ChartMetricBasis;
  summaryTable:
    | { supported: false }
    | ({ supported: true } & ChartSummaryTableMetadata);
}

export interface ListChartTypesResultMode
  extends Omit<ChartOutputModeCapability, "summaryTable"> {
  chartTypeCount: number;
  summaryTable: ChartOutputModeCapability["summaryTable"];
}

export interface ListChartTypesResult {
  total: number;
  returned: number;
  /** Output-mode contracts matching the requested filter. */
  modes: ListChartTypesResultMode[];
  /** Same order as `CHART_CAPABILITIES` for stability across calls. */
  entries: ListChartTypesResultEntry[];
  /** Human-readable note when results were truncated by `maxResults`. */
  note?: string;
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

export const LIST_CHART_TYPES_TOOL: ChartCapabilityToolDef = {
  name: "list_chart_types",
  description:
    `List chart types and output-mode contracts for ${CHART_OUTPUT_MODES.map(
      (mode) => `${mode.id} (${mode.dataSource} → ${mode.landingLocation})`,
    ).join(", ")}. ` +
    "Read metricBasis when present: VAL_PE2 is trailing/LTM P/E and must not be used for forward/NTM P/E. " +
    "Use this when the user asks 'what charts can you make?' or when you're " +
    "unsure which chartType code is appropriate for their request.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      userIntent: {
        type: "string",
        description:
          "Free-form description of what the user wants to visualize. " +
          "Used as a relevance hint when no other filter is supplied.",
      },
      outputMode: {
        type: "string",
        enum: ["range_chart", "active_graph", "native_chart", "any"],
        description:
          "Filter by output contract: range_chart uses workbook cells in the current " +
          "workbook; active_graph uses FactSet parameters in the current workbook; " +
          "native_chart uses FactSet parameters in a new workbook.",
      },
      category: {
        type: "string",
        enum: [
          "price_volume_fx",
          "valuation",
          "estimates",
          "financials",
          "fixed_income",
          "size",
          "native_column",
          "native_bar",
          "native_line",
          "native_pie",
          "native_area",
          "native_scatter",
          "native_other",
        ],
        description: "Filter by capability category.",
      },
      summaryTable: {
        type: "string",
        enum: ["supported", "unsupported", "any"],
        description:
          "Filter by Chart Creator native-workbook summary-table applicability.",
      },
      maxResults: {
        type: "integer",
        minimum: 1,
        maximum: 51,
        description: "Cap on the number of entries returned. Default 20.",
      },
    },
  },
};

export const CHART_CAPABILITY_TOOLS: readonly ChartCapabilityToolDef[] = [
  LIST_CHART_TYPES_TOOL,
];

// ---------------------------------------------------------------------------
// Executor
// ---------------------------------------------------------------------------

/**
 * Cap on the number of entries any single call can return. Matches
 * the registry size today (51 = 27 FactSet + 24 native) so a caller
 * passing `maxResults: 100` doesn't read as "the catalog has 100
 * entries and 49 are hidden".
 */
const HARD_MAX_RESULTS = CHART_CAPABILITIES.length;
const DEFAULT_MAX_RESULTS = 20;

/**
 * Execute `list_chart_types` server-side, returning the structured
 * result the model will see as the tool response. Pure function over
 * the registry — no I/O, no host calls, safe to run repeatedly.
 *
 * `userIntent` is a safety filter rather than a general relevance ranker:
 * an explicit forward/NTM P/E request excludes trailing/LTM capabilities.
 * Other intent-based ranking remains advisory.
 */
export function executeListChartTypes(input: ListChartTypesInput): ListChartTypesResult {
  const max = clampMaxResults(input.maxResults);
  const outputMode = input.outputMode === "any" ? undefined : input.outputMode;
  const summaryTable =
    input.summaryTable === "any" ? undefined : input.summaryTable;
  const filteredByFields = listChartCapabilities({
    outputMode,
    category: input.category,
  }).filter((capability) => {
    if (summaryTable === "supported") return Boolean(capability.summaryTable);
    if (summaryTable === "unsupported") return !capability.summaryTable;
    return true;
  });
  const filtered = isExplicitForwardPeRequest(input.userIntent)
    ? filteredByFields.filter(
        (capability) => capability.metricBasis !== "trailing_ltm",
      )
    : filteredByFields;
  const entries = filtered.slice(0, max).map(trimEntry);

  const result: ListChartTypesResult = {
    total: filtered.length,
    returned: entries.length,
    modes: listResultModes(outputMode),
    entries,
  };
  if (filtered.length > entries.length) {
    result.note =
      `${filtered.length - entries.length} additional capabilities matched ` +
      `the filter but were truncated by maxResults=${max}. Tighten the filter ` +
      `(set \`category\` or \`outputMode\`) to see more.`;
  }
  return result;
}

function clampMaxResults(raw: number | undefined): number {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_MAX_RESULTS;
  }
  return Math.min(Math.floor(raw), HARD_MAX_RESULTS);
}

function trimEntry(c: ChartCapability): ListChartTypesResultEntry {
  return {
    id: c.id,
    displayName: c.displayName,
    outputModes: c.outputModes,
    category: c.category,
    description: c.description,
    ...(c.metricBasis ? { metricBasis: c.metricBasis } : {}),
    summaryTable: c.summaryTable
      ? { supported: true, ...c.summaryTable }
      : { supported: false },
  };
}

function listResultModes(
  outputMode: ChartOutputMode | undefined,
): ListChartTypesResultMode[] {
  return CHART_OUTPUT_MODES.filter(
    (mode) => !outputMode || mode.id === outputMode,
  ).map((mode) => ({
    ...mode,
    chartTypeCount: listChartCapabilities({ outputMode: mode.id }).length,
  }));
}

/**
 * Dispatch a tool call by name to its executor. Returns the JSON
 * string the model expects as the tool's `output`.
 *
 * Unknown tool names produce a structured error object (not a thrown
 * exception) so the caller's tool-loop driver can hand it back to
 * the model as the tool response instead of aborting the request.
 */
export function executeChartCapabilityTool(
  name: string,
  rawInput: unknown,
): string {
  if (name === LIST_CHART_TYPES_TOOL.name) {
    const input = normaliseListInput(rawInput);
    return JSON.stringify(executeListChartTypes(input));
  }
  return JSON.stringify({
    error: `Unknown chart capability tool: "${name}". ` +
      `Available tools: ${CHART_CAPABILITY_TOOLS.map((t) => t.name).join(", ")}.`,
  });
}

function normaliseListInput(raw: unknown): ListChartTypesInput {
  if (raw === null || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const out: ListChartTypesInput = {};
  if (typeof r.userIntent === "string") out.userIntent = r.userIntent;
  if (typeof r.outputMode === "string") {
    out.outputMode = r.outputMode as ListChartTypesInput["outputMode"];
  }
  if (typeof r.category === "string") out.category = r.category as ChartCategory;
  if (typeof r.summaryTable === "string") {
    out.summaryTable = r.summaryTable as ListChartTypesInput["summaryTable"];
  }
  if (typeof r.maxResults === "number") out.maxResults = r.maxResults;
  return out;
}
